import React, { useState, useEffect, useRef } from "react";
import Header from "../../../Components/Header";
import Seo from "../../../Components/SeoComponent/Seo";
import { showSuccessToast, showErrorToast } from "../../../utils/toastUtils";
import { fetchProfile } from "../../../Services/ApiTestService/aadharTestService";
import { verifyRcAdvanceDetails } from "../../../Services/ApiTestService/apiTestService";
import { InputWithLabel, Button } from "../../../Components/Tags/Tags";
import Loader from "../../../Loader";
import { Link } from "react-router-dom";
import { IoArrowBackCircleOutline, IoPrintOutline, IoCopyOutline, IoDocumentTextOutline } from "react-icons/io5";
import { useService } from "../../../context/ServiceContext";

const RcVerificationAdvance = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [rcData, setRcData] = useState({});
  const [showRcData, setShowRcData] = useState(false);
  const [errors, setErrors] = useState({});
  const [profileData, setProfileData] = useState({});
  const [regNo, setRegNo] = useState("");
  const { serviceName } = useService();
  
  // Reference for the formatted data container (used for copying text)
  const summaryRef = useRef(null);

  // Fetch profile data on mount
  useEffect(() => {
    const fetchProfileData = async () => {
      try {
        setIsLoading(true);
        const response = await fetchProfile();
        setProfileData(response.data);
      } catch (error) {
        console.error("Error fetching profile data:", error);
        showErrorToast("Failed to fetch profile data.");
      } finally {
        setIsLoading(false);
      }
    };

    fetchProfileData();
  }, []);

  const validateForm = (regNo) => {
    const errors = {};
    if (!regNo) {
      errors.regNo = "Registration No. is required.";
    }
    return errors;
  };

  const handleSubmit = async () => {
    const validationErrors = validateForm(regNo);
    setErrors(validationErrors);
    if (Object.keys(validationErrors).length > 0) return;

    try {
      setIsLoading(true);

      const response = await verifyRcAdvanceDetails({
        api_id: profileData?.user_id,
        api_key: profileData?.api_key,
        token_id: profileData?.token_id,
        regNo: regNo,
      });

      const statusCode = response?.status?.code || response?.status;

      if (statusCode === 200) {
        const rcData = response?.data;

        if (rcData) {
          setRcData(rcData);
          setShowRcData(true);
          showSuccessToast(response.message, "Rc Details fetched successfully.");
          setRegNo("");
          setErrors({});
        } else {
          showErrorToast("Unexpected API format.");
        }
      } else if (statusCode === 422) {
        showErrorToast(response.data.message || "Invalid format");
      } else if (statusCode === 500) {
        showErrorToast(response.data.message || "Server error");
      } else {
        showErrorToast(response.status?.message || "Something went wrong");
      }
    } catch (error) {
      console.error("Rc Advance Details Error:", error);
      showErrorToast("Failed to verify Rc Advance Details Error.");
    } finally {
      setIsLoading(false);
    }
  };

  // --- NEW FEATURES: COPY, EXCEL, PRINT ---

  const handleCopy = () => {
    if (summaryRef.current) {
      const textToCopy = summaryRef.current.innerText;
      navigator.clipboard.writeText(textToCopy)
        .then(() => showSuccessToast("Data copied to clipboard!"))
        .catch(() => showErrorToast("Failed to copy data."));
    }
  };

  const exportToExcel = () => {
    if (!rcData) return;
    
    // Map JSON to a CSV table format
    const csvData = [
      ["Field", "Value"],
      ["Owner Name", rcData.ownerName],
      ["Father Name", rcData.fatherName],
      ["Status", rcData.status],
      ["Registration No", rcData.regNo],
      ["State", rcData.state],
      ["RTO", rcData.rto],
      ["Registration Date", rcData.regDate],
      ["Chassis No", rcData.chassisNo],
      ["Engine No", rcData.engineNo],
      ["Vehicle Class", rcData.vehicleClass],
      ["Vehicle Color", rcData.vehicleColor],
      ["Maker", rcData.maker],
      ["Maker Modal", rcData.makerModal],
      ["Body Type Desc", rcData.bodyTypeDesc],
      ["Fuel Type", rcData.fuelType],
      ["Fuel Norms", rcData.fuelNorms],
      ["Permanent Address", rcData.permanentAddress],
      ["Present Address", rcData.presentAddress],
      ["Fitness UpTo", rcData.fitnessUpto],
      ["Tax UpTo", rcData.taxUpto],
      ["Insurance Company", rcData.insCompany],
      ["Insurance Upto", rcData.insUpto],
      ["Policy No", rcData.policyNo],
      ["Puc No", rcData.pucNo],
      ["Puc Upto", rcData.pucUpto],
      ["Manufacture Month Year", rcData.manufacturedMonthYear],
      ["Unladen Weight", rcData.unladenWeight],
      ["No of Cylinders", rcData.noCylinders],
      ["Cubic Cap", rcData.cubicCap],
      ["No Of Seats", rcData.noOfSeats],
      ["Vehicle Category", rcData.vehicleCategory],
      ["RTO Code", rcData.rtoCode]
    ];

    // Convert array to CSV string
    const csvContent = csvData.map(row => row.map(item => `"${item || ''}"`).join(",")).join("\n");
    
    // Create Blob and Download Link
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", `RC_Details_${rcData.regNo || 'Export'}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handlePrint = () => {
    window.print();
  };

  return (
    <>
      <Seo />
      {isLoading && <Loader />}
      
      {/* Hide Header when printing */}
      <div className="print:hidden">
        <Header heading={serviceName} />
      </div>

      {/* Hide Form section when printing */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 justify-center md:ml-80 ml-0 p-4 print:hidden">
        <div className="col-span-2 md:col-span-6 flex justify-end">
          <Link
            to="/TestMaster"
            className="flex items-center gap-1 text-white bg-green-600 hover:bg-green-700 px-1 py-1 rounded-md font-semibold shadow-md transition duration-300"
          >
            <span>Go Back</span>
            <IoArrowBackCircleOutline size={24} />
          </Link>
        </div>

        <InputWithLabel
          type="text"
          label="Registration No."
          placeholder="Enter Registration No."
          className="p-1 rounded shadow-lg shadow-blue-500/50 border border-blue-600"
          name="regNo"
          id="regNo"
          required={true}
          value={regNo}
          onChange={(e) => setRegNo(e.target.value)}
          error={errors.regNo}
        />

        <div>
          <Button
            type="button"
            className="text-center text-white bg-blue-900 shadow-lg bg-gradient-to-r from-blue-400 via-blue-1200 to-blue-900 font-semibold rounded-md px-2 py-1 mt-5 text-md hover:from-blue-900 hover:to-blue-400 border-4 ms-2"
            id="verifyRC"
            name="verifyRC"
            text="Verify"
            onClick={handleSubmit}
          />
        </div>
      </div>

      {/* Hide Instructions when printing */}
      <div className="max-w-2xl mx-auto p-2 bg-white rounded-2xl shadow-lg mt-3 mb-6 print:hidden">
        <h2 className="text-xl font-bold text-center text-gray-800 mb-2">Rc Advance V2 Steps</h2>
        <ol className="list-decimal list-inside text-gray-700 space-y-1">
          <li>Enter your registration number.</li>
          <li>Click on "Verify" to check RC details.</li>
        </ol>
        <div className="mt-2">
          <h3 className="text-xl font-semibold text-gray-800 mb-2">Important Notes:</h3>
          <ul className="list-disc list-inside text-gray-600 space-y-1">
            <li>Do not refresh the page during the verification process.</li>
          </ul>
        </div>
      </div>

      {showRcData && rcData && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 gap-4 px-4 print:grid-cols-1 print:px-0">
          
          {/* Summary Card - Expanded for print layout */}
          <div className="bg-gradient-to-br from-blue-50 to-white border border-blue-200 rounded-2xl shadow-xl p-4 max-h-64 overflow-scroll print:max-h-none print:overflow-visible print:shadow-none print:border-0 print:p-0">
            
            {/* Header with Action Buttons */}
            <div className="flex justify-between items-center mb-3 border-b pb-2 print:border-none">
              <h3 className="text-blue-800 font-semibold text-xl">Rc Data Summary</h3>
              
              {/* Action Buttons (Hidden during Print) */}
              <div className="flex gap-2 print:hidden">
                <button onClick={handleCopy} title="Copy Formatted Data" className="flex items-center gap-1 bg-gray-200 hover:bg-gray-300 text-gray-700 px-2 py-1 rounded text-sm transition">
                  <IoCopyOutline /> Copy
                </button>
                <button onClick={exportToExcel} title="Export to Excel" className="flex items-center gap-1 bg-green-100 hover:bg-green-200 text-green-700 px-2 py-1 rounded text-sm transition">
                  <IoDocumentTextOutline /> Excel
                </button>
                <button onClick={handlePrint} title="Print Data" className="flex items-center gap-1 bg-blue-100 hover:bg-blue-200 text-blue-700 px-2 py-1 rounded text-sm transition">
                  <IoPrintOutline /> Print
                </button>
              </div>
            </div>

            {/* Formatted Data mapped to a Reference for copying */}
            <div className="text-sm text-gray-700 space-y-1" ref={summaryRef}>
              <p><strong>Owner Name :</strong> {rcData.ownerName}</p>
              <p><strong>Father Name :</strong> {rcData.fatherName}</p>
              <p><strong>Status :</strong> {rcData.status}</p>
              <p><strong>Registration No :</strong> {rcData.regNo}</p>
              <p><strong>State :</strong> {rcData.state}</p>
              <p><strong>RTO :</strong> {rcData.rto}</p>
              <p><strong>Registration Date :</strong> {rcData.regDate}</p>
              <p><strong>Chassis No :</strong> {rcData.chassisNo}</p>
              <p><strong>Engine No :</strong> {rcData.engineNo}</p>
              <p><strong>Vehicle Class :</strong> {rcData.vehicleClass}</p>
              <p><strong>Vehicle Color :</strong> {rcData.vehicleColor}</p>
              <p><strong>Maker :</strong> {rcData.maker}</p>
              <p><strong>Maker Modal :</strong> {rcData.makerModal}</p>
              <p><strong>Body Type Desc :</strong> {rcData.bodyTypeDesc}</p>
              <p><strong>Fuel Type :</strong> {rcData.fuelType}</p>
              <p><strong>Fuel Norms :</strong> {rcData.fuelNorms}</p>
              <p><strong>Permanent Address :</strong> {rcData.permanentAddress}</p>
              <p><strong>Present Address :</strong> {rcData.presentAddress}</p>
              <p><strong>Fitness UpTo :</strong> {rcData.fitnessUpto}</p>
              <p><strong>Tax UpTo :</strong> {rcData.taxUpto}</p>
              <p><strong>Insurance Company :</strong> {rcData.insCompany}</p>
              <p><strong>Insurance Upto :</strong> {rcData.insUpto}</p>
              <p><strong>Policy No :</strong> {rcData.policyNo}</p>
              <p><strong>Puc No :</strong> {rcData.pucNo}</p>
              <p><strong>Puc Upto :</strong> {rcData.pucUpto}</p>
              <p><strong>Manufacture Month Year :</strong> {rcData.manufacturedMonthYear}</p>
              <p><strong>Unladen Weight :</strong> {rcData.unladenWeight}</p>
              <p><strong>No of Cylinders :</strong> {rcData.noCylinders}</p>
              <p><strong>Cubic Cap :</strong> {rcData.cubicCap}</p>
              <p><strong>No Of Seats :</strong> {rcData.noOfSeats}</p>
              <p><strong>Vehicle Category :</strong> {rcData.vehicleCategory}</p>
              <p><strong>RTO Code :</strong> {rcData.rtoCode}</p>          
            </div>
          </div>

          {/* JSON Preview - Hidden during print */}
          <div className="bg-gradient-to-br from-blue-50 to-white border border-blue-200 rounded-2xl shadow-xl p-2 print:hidden">
            <h2 className="text-lg font-semibold text-blue-800 mb-1">Rc Details JSON Data</h2>
            <pre className="bg-gray-100 p-2 rounded text-sm overflow-y-auto max-h-[200px]">
              {JSON.stringify(rcData, null, 2)}
            </pre>
          </div>
        </div>
      )}
    </>
  );
};

export default RcVerificationAdvance;